import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Mail, CheckCircle2, XCircle, Loader2, Filter, AlertCircle, Inbox,
} from "lucide-react";

const KIND_LABELS = {
  client_confirmation: { label: "Confirmation client", color: "bg-fegreen-light text-fegreen" },
  admin_notification: { label: "Notification admin", color: "bg-blue-100 text-blue-700" },
  smtp_test: { label: "Test SMTP", color: "bg-purple-100 text-purple-700" },
  manual: { label: "Manuel", color: "bg-gray-100 text-fegray" },
};

const STATUS_CFG = {
  sent: { label: "Envoyé", color: "text-fegreen", Icon: CheckCircle2 },
  failed: { label: "Échec", color: "text-red-500", Icon: XCircle },
  skipped: { label: "Ignoré", color: "text-fegray", Icon: AlertCircle },
  pending: { label: "En attente", color: "text-amber-500", Icon: Loader2 },
};

export default function AdminEmailHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (kindFilter) params.set("kind", kindFilter);
      const r = await api.get(`/admin/email-history?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, kindFilter]);

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Communications</p>
      <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark mb-2">Historique des emails</h1>
      <p className="text-sm text-fegray mb-7">Tous les emails envoyés depuis votre pod, avec leur statut de livraison.</p>

      <div className="grid grid-cols-3 gap-4 mb-7">
        <Stat label="Total envoyé" value={data?.total ?? "—"} />
        <Stat label="Avec succès" value={data?.sent ?? "—"} accent="green" />
        <Stat label="En échec" value={data?.failed ?? "—"} accent={data?.failed ? "red" : null} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span className="text-[10px] uppercase tracking-wider text-fegray font-bold inline-flex items-center gap-1.5">
          <Filter className="w-3 h-3" /> Filtrer
        </span>
        <FilterPill active={!statusFilter} onClick={() => setStatusFilter("")} label="Tous les statuts" testid="email-filter-all" />
        <FilterPill active={statusFilter === "sent"} onClick={() => setStatusFilter("sent")} label="Envoyés" testid="email-filter-sent" />
        <FilterPill active={statusFilter === "failed"} onClick={() => setStatusFilter("failed")} label="Échecs" testid="email-filter-failed" />
        <span className="mx-1 text-fegray/30">·</span>
        <FilterPill active={!kindFilter} onClick={() => setKindFilter("")} label="Tous les types" testid="email-kind-all" />
        <FilterPill active={kindFilter === "client_confirmation"} onClick={() => setKindFilter("client_confirmation")} label="Confirmations" testid="email-kind-client" />
        <FilterPill active={kindFilter === "admin_notification"} onClick={() => setKindFilter("admin_notification")} label="Notifications admin" testid="email-kind-admin" />
      </div>

      {loading && !data ? (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-fegreen mx-auto" /></div>
      ) : !data || data.items.length === 0 ? (
        <div className="fe-card p-12 text-center text-fegray">
          <Inbox className="w-8 h-8 mx-auto text-fegray/30 mb-3" />
          Aucun email envoyé pour le moment.
        </div>
      ) : (
        <div className="fe-card overflow-hidden" data-testid="email-history-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-fegray-light text-xs uppercase tracking-wider text-fegray border-b border-gray-100">
                <th className="text-left p-4">Statut</th>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Destinataire</th>
                <th className="text-left p-4">Sujet</th>
                <th className="text-left p-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const cfg = STATUS_CFG[it.status] || STATUS_CFG.pending;
                const kind = KIND_LABELS[it.kind] || KIND_LABELS.manual;
                return (
                  <tr key={it.id} className="border-b border-gray-50 last:border-0 hover:bg-fegray-light/30" data-testid={`email-row-${it.id}`}>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${cfg.color}`}>
                        <cfg.Icon className="w-3.5 h-3.5" /> {cfg.label}
                      </span>
                      {it.error && <div className="text-[10px] text-red-500 mt-1 max-w-[260px] truncate" title={it.error}>{it.error}</div>}
                    </td>
                    <td className="p-4">
                      <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-bold ${kind.color}`}>
                        {kind.label}
                      </span>
                    </td>
                    <td className="p-4 text-fegray-dark font-mono text-xs">{it.to}</td>
                    <td className="p-4 text-fegray-dark max-w-[280px] truncate" title={it.subject}>{it.subject}</td>
                    <td className="p-4 text-fegray text-xs whitespace-nowrap">
                      {new Date(it.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-2xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.06)] ${
      accent === "green" ? "bg-fegreen text-white" : accent === "red" ? "bg-red-50 border border-red-200" : "bg-white"
    }`}>
      <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${accent === "green" ? "text-white/80" : "text-fegray"}`}>{label}</div>
      <div className={`text-3xl font-extrabold ${accent === "green" ? "text-white" : accent === "red" ? "text-red-600" : "text-fegray-dark"}`}>{value}</div>
    </div>
  );
}

function FilterPill({ active, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] font-bold px-3 py-1 rounded-full transition-all ${
        active ? "bg-fegreen text-white" : "bg-white border border-gray-200 text-fegray-dark hover:border-fegreen"
      }`}
    >
      {label}
    </button>
  );
}
